import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LogoSvg from "../../common/assets/images/logo1.svg";
import {
  HomeIcon,
  BookOpenIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  FolderIcon,
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { localStorageRemoveItem } from "../../common/localStorage";
import {
  checkJwtExistsAndExpired,
  getUserInfo,
  logoutSession,
} from "../../services/AuthService";
import { toast } from "react-toastify";
import { PhoneCall } from "lucide-react";
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 24 24"
  fill="currentColor"
  class="size-6"
>
  <path
    fill-rule="evenodd"
    d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
    clip-rule="evenodd"
  />
</svg>;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const user = getUserInfo();
  const authenticated = checkJwtExistsAndExpired();

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logoutSession();
      localStorageRemoveItem("jwt");
      localStorageRemoveItem("user");
      setOpenUserMenu(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error(error?.response?.data?.message || "Đăng xuất thất bại");
    }
  };

  const accountRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        openUserMenu &&
        accountRef.current &&
        !accountRef.current.contains(e.target)
      ) {
        setOpenUserMenu(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [openUserMenu]);

  return (
    <header className="bg-[#f9dfe8] py-4 px-6 shadow-sm">
      <div className="max-w-[85rem] mx-auto flex items-center justify-between gap-4 flex-wrap md:flex-nowrap">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3 mr-[55px]">
          <img src={LogoSvg} alt="PIMAX" className="w-12 h-12" />
          <span className="text-3xl font-extrabold text-[#db3b6d]">PIMAX</span>
        </Link>

        {/* Navigation - desktop */}
        <div className="bg-[#f1c0cf] px-4 sm:px-6 py-4 rounded-md shadow-md border border-[#e5a7b7] hidden md:block mx-auto overflow-x-auto">
          <nav className="flex items-center space-x-6 sm:space-x-8 whitespace-nowrap">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 transition-colors ${
                isActive("/")
                  ? "text-[#db3b6d]"
                  : "text-[#4b4b4b] hover:text-[#db3b6d]"
              }`}
            >
              <HomeIcon className="w-5 h-5" />
              <span className="font-medium">TRANG CHỦ</span>
            </Link>
            <Link
              to="/courses"
              className={`inline-flex items-center gap-2 transition-colors ${
                isActive("/courses")
                  ? "text-[#db3b6d]"
                  : "text-[#4b4b4b] hover:text-[#db3b6d]"
              }`}
            >
              <BookOpenIcon className="w-5 h-5" />
              <span className="font-medium">KHÓA HỌC</span>
            </Link>
            <Link
              to="/practice-room"
              className={`inline-flex items-center gap-2 transition-colors ${
                isActive("/practice-room")
                  ? "text-[#db3b6d]"
                  : "text-[#4b4b4b] hover:text-[#db3b6d]"
              }`}
            >
              <PencilSquareIcon className="w-5 h-5" />
              <span className="font-medium">PHÒNG LUYỆN ĐỀ</span>
            </Link>
            <Link
              to="/materials?type=tsa_mock"
              className={`inline-flex items-center gap-2 transition-colors ${
                location.pathname.startsWith("/materials")
                  ? "text-[#db3b6d]"
                  : "text-[#4b4b4b] hover:text-[#db3b6d]"
              }`}
            >
              <DocumentTextIcon className="w-5 h-5" />
              <span className="font-medium">TÀI LIỆU</span>
            </Link>
            <Link
              to="https://www.facebook.com/dducpimax1994"
              target="_blank"
              className={`inline-flex items-center gap-2 transition-colors ${
                isActive("/contact")
                  ? "text-[#db3b6d]"
                  : "text-[#4b4b4b] hover:text-[#db3b6d]"
              }`}
            >
              <PhoneIcon className="w-5 h-5" />
              <span className="font-medium">LIÊN HỆ</span>
            </Link>
          </nav>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(true)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#db3b6d] text-[#db3b6d]"
          aria-label="Open menu"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Right: Auth or User */}
        {user && authenticated ? (
          <div className="ml-[5px] flex items-center gap-3 md:gap-4 flex-shrink-0 whitespace-nowrap">
            {/* Account button + dropdown */}
            <div className="relative" ref={accountRef}>
              <button
                onClick={() => setOpenUserMenu((o) => !o)}
                className={`inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg font-medium transition-all h-10 border-2 ${
                  openUserMenu
                    ? "bg-[#db3b6d] text-white border-[#db3b6d]"
                    : "border-[#4b4b4b] text-[#4b4b4b] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d]"
                }`}
              >
                <UserIcon className="w-5 h-5" /> TÀI KHOẢN
              </button>
              {openUserMenu && (
                <div className="absolute left-0 md:left-auto md:right-0 mt-2 w-64 max-w-[92vw] bg-white border border-[#e5a7b7] rounded-md shadow-lg z-50">
                  <div className="px-4 py-3 text-sm text-gray-700">
                    <div className="font-semibold text-[#db3b6d]">
                      {user.fullName || user.username || "Người dùng"}
                    </div>
                    <div className="text-gray-500 truncate">{user.email}</div>
                    <div className="mt-1 text-gray-600">
                      Mã định danh:{" "}
                      <span className="font-medium">
                        {user.identifier || "—"}
                      </span>
                    </div>
                    <div className="mt-1 text-gray-600">
                      Trạng thái:{" "}
                      {user.premium ? (
                        <span className="text-[#db3b6d] font-semibold inline-flex items-center gap-1">
                          Premium <span>👑</span>
                        </span>
                      ) : (
                        <span className="text-[#4b4b4b] font-semibold inline-flex items-center gap-1">
                          Tự do
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-[#f1c0cf]" />
                  <Link
                    to="#"
                    className="block px-4 py-2 text-sm hover:bg-[#f9dfe8] text-gray-700"
                  >
                    Thông tin tài khoản
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left block px-4 py-2 text-sm hover:bg-[#f9dfe8] text-gray-700"
                  >
                    Đăng xuất
                  </button>
                </div>
              )}
            </div>

            {/* Divider */}
            <span className="hidden md:block h-8 w-px bg-[#4b4b4b]/60" />

            {/* Go learn */}
            <Link
              to="/practice-room"
              className="inline-flex items-center gap-2 px-3 md:px-6 py-2 rounded-lg font-medium transition-all h-10 bg-[#db3b6d] text-white hover:bg-[#c63262] shadow"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" /> VÀO HỌC
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-3 md:gap-4 flex-shrink-0 whitespace-nowrap ml-[5px]">
            <Link
              to="/login"
              className={`inline-flex items-center gap-2 px-4 py-2 md:px-6 rounded-lg font-medium transition-all h-10 border-2 ${
                isActive("/login")
                  ? "bg-[#db3b6d] text-white border-[#db3b6d]"
                  : "border-[#4b4b4b] text-[#4b4b4b] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d]"
              }`}
            >
              <UserIcon className="w-5 h-5" /> ĐĂNG NHẬP
            </Link>
            <Link
              to="/register"
              className={`inline-flex items-center gap-2 px-4 py-2 md:px-6 rounded-lg font-medium transition-all h-10 border-2 ${
                isActive("/register")
                  ? "bg-[#db3b6d] text-white border-[#db3b6d]"
                  : "border-[#4b4b4b] text-[#4b4b4b] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d]"
              }`}
            >
              <UserPlusIcon className="w-5 h-5" /> ĐĂNG KÍ
            </Link>
          </div>
        )}

        {/* Sidebar Mobile Menu */}
        {isMobileMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="fixed z-50 top-0 left-0 h-full w-72 bg-[#f1c0cf] shadow-xl border-r border-[#e5a7b7] p-4">
              <div className="flex items-center justify-between mb-4">
                <Link to="/" className="flex items-center space-x-3 mr-[55px]">
                  <img src={LogoSvg} alt="PIMAX" className="w-10 h-10" />
                  <span className="text-2xl font-extrabold text-[#db3b6d]">
                    PIMAX
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="inline-flex w-9 h-9 items-center justify-center rounded-md border border-[#db3b6d] text-[#db3b6d]"
                  aria-label="Close menu"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex flex-col gap-3">
                <Link
                  onClick={() => setIsMobileMenuOpen(false)}
                  to="/"
                  className={`inline-flex items-center gap-2 ${
                    isActive("/")
                      ? "text-[#db3b6d]"
                      : "text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  <HomeIcon className="w-5 h-5" />
                  <span className="font-medium">TRANG CHỦ</span>
                </Link>
                <Link
                  onClick={() => setIsMobileMenuOpen(false)}
                  to="/courses"
                  className={`inline-flex items-center gap-2 ${
                    isActive("/courses")
                      ? "text-[#db3b6d]"
                      : "text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  <BookOpenIcon className="w-5 h-5" />
                  <span className="font-medium">KHÓA HỌC</span>
                </Link>
                <Link
                  onClick={() => setIsMobileMenuOpen(false)}
                  to="/practice-room"
                  className={`inline-flex items-center gap-2 ${
                    isActive("/practice-room")
                      ? "text-[#db3b6d]"
                      : "text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  <PencilSquareIcon className="w-5 h-5" />
                  <span className="font-medium">PHÒNG LUYỆN ĐỀ</span>
                </Link>
                <Link
                  onClick={() => setIsMobileMenuOpen(false)}
                  to="/materials?type=tsa_mock"
                  className={`inline-flex items-center gap-2 ${
                    location.pathname.startsWith("/materials")
                      ? "text-[#db3b6d]"
                      : "text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span className="font-medium">TÀI LIỆU</span>
                </Link>
                <Link
                  onClick={() => setIsMobileMenuOpen(false)}
                  to="https://www.facebook.com/dducpimax1994"
                  target="_blank"
                  className={`inline-flex items-center gap-2 ${
                    isActive("/contact")
                      ? "text-[#db3b6d]"
                      : "text-[#4b4b4b] hover:text-[#db3b6d]"
                  }`}
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span className="font-medium">LIÊN HỆ</span>
                </Link>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
